import { useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { useCreate, useDataProvider, useNotify, useRedirect } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import type { LeadParseDraft } from './leadApi';

import { defaultLeadStatuses } from '@/root/defaultConfiguration';
import type { LeadStatus } from '@/types';
import {
    INTAKE_FILE_ACCEPT,
    INTAKE_IMAGE_ACCEPT,
    partitionIntakeFiles,
} from './leadIntakeFiles';
import {
    LeadNextStepsEditor,
    blankNextStep,
    newStepClientKey,
    type NextStepDraft,
} from './LeadNextStepsEditor';

const emptySteps: NextStepDraft[] = [];

const pickRecorderMime = () => {
    if (typeof MediaRecorder === 'undefined') return '';
    const types = [
        'audio/mp4',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/aac',
        'audio/webm;codecs=opus',
        'audio/webm',
    ];
    return types.find(type => MediaRecorder.isTypeSupported(type)) || '';
};

export const LeadCreate = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [create, { isPending: isSaving }] = useCreate();
    const photoInputRef = useRef<HTMLInputElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);

    const [text, setText] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [recording, setRecording] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [sourcePath, setSourcePath] = useState<string | null>(null);
    const [sourcePaths, setSourcePaths] = useState<string[]>([]);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [requirements, setRequirements] = useState('');
    const [status, setStatus] = useState<LeadStatus>('pending');
    const [nextSteps, setNextSteps] = useState<NextStepDraft[]>(emptySteps);
    const [reviewOpen, setReviewOpen] = useState(false);

    const hasCapture = Boolean(text.trim() || files.length);

    const addFiles = (incoming: File[]) => {
        const { accepted, rejected } = partitionIntakeFiles(incoming);
        if (rejected.length) {
            notify('Only JPEG, PNG, or audio files are allowed', { type: 'warning' });
        }
        if (accepted.length) setFiles(prev => [...prev, ...accepted]);
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mime = pickRecorderMime();
            const recorder = mime
                ? new MediaRecorder(stream, { mimeType: mime })
                : new MediaRecorder(stream);
            chunksRef.current = [];
            recorder.ondataavailable = event => {
                if (event.data.size) chunksRef.current.push(event.data);
            };
            recorder.onstop = () => {
                stream.getTracks().forEach(track => track.stop());
                const type = recorder.mimeType || mime || 'audio/webm';
                const blob = new Blob(chunksRef.current, { type });
                const ext = /mp4|aac|m4a/i.test(type) ? 'm4a' : 'webm';
                setFiles(prev => [
                    ...prev,
                    new File([blob], `intake.${ext}`, { type }),
                ]);
                setRecording(false);
            };
            recorderRef.current = recorder;
            recorder.start();
            setRecording(true);
        } catch {
            notify('Microphone is not available', { type: 'warning' });
        }
    };

    const stopRecording = () => {
        recorderRef.current?.stop();
        recorderRef.current = null;
    };

    const handleParse = async () => {
        if (!hasCapture) return;
        setParsing(true);
        try {
            const draft: LeadParseDraft = await dataProvider.parseLeadIntake({
                text: text.trim() || undefined,
                files,
            });
            setName(draft.lead.name || '');
            setEmail(draft.lead.email || '');
            setPhoneNumber(draft.lead.phone_number || '');
            setRequirements(draft.lead.requirements || '');
            setSourcePath(draft.source_path);
            setSourcePaths(
                draft.source_paths?.length
                    ? draft.source_paths
                    : draft.source_path
                      ? [draft.source_path]
                      : []
            );
            setNextSteps(
                draft.next_steps.map(step => ({
                    clientKey: newStepClientKey(),
                    name: step.name,
                    notify: step.notify,
                    notify_at: step.notify_at,
                    status: 'pending',
                }))
            );
            setReviewOpen(true);
            notify('Intake read. Review and save.', { type: 'info' });
        } catch (err) {
            notify(err instanceof Error ? err.message : 'Could not read intake', {
                type: 'warning',
            });
        } finally {
            setParsing(false);
        }
    };

    const handleSave = () => {
        create(
            'leads',
            {
                data: {
                    name: name.trim() || null,
                    email: email.trim() || null,
                    phone_number: phoneNumber.trim() || null,
                    requirements: requirements.trim() || null,
                    status,
                    source_path: sourcePath,
                    source_paths: sourcePaths.length ? sourcePaths : null,
                    next_steps: nextSteps
                        .filter(step => step.name.trim())
                        .map(step => ({
                            name: step.name.trim(),
                            notify: step.notify,
                            notify_at: step.notify_at,
                            status: step.status || 'pending',
                        })),
                },
            },
            {
                onSuccess: data => {
                    notify('Lead created', { type: 'info' });
                    redirect('show', 'leads', data.id);
                },
                onError: () => notify('Failed to create lead', { type: 'warning' }),
            }
        );
    };

    const handleCreateFromScratch = () => {
        setReviewOpen(true);
        if (nextSteps.length === 0) {
            setNextSteps([blankNextStep()]);
        }
    };

    return (
        <Box mt={2} mb={2} width="100%">
            <Card>
                <CardContent>
                    <Typography variant="h5" mb={2}>
                        New lead
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            label="Message, email, or notes"
                            value={text}
                            onChange={e => setText(e.target.value)}
                            fullWidth
                            multiline
                            minRows={4}
                        />
                        <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept={INTAKE_IMAGE_ACCEPT}
                                    capture="environment"
                                    hidden
                                    onChange={e => {
                                        const picked = e.target.files?.[0];
                                        if (picked) addFiles([picked]);
                                        e.target.value = '';
                                    }}
                                />
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={INTAKE_FILE_ACCEPT}
                                    multiple
                                    hidden
                                    onChange={e => {
                                        addFiles(Array.from(e.target.files || []));
                                        e.target.value = '';
                                    }}
                                />
                                <IconButton
                                    aria-label="Take or choose photo"
                                    onClick={() => photoInputRef.current?.click()}
                                    size="large"
                                >
                                    <ImageIcon />
                                </IconButton>
                                <IconButton
                                    aria-label="Add photos or audio files"
                                    onClick={() => fileInputRef.current?.click()}
                                    size="large"
                                >
                                    <AttachFileIcon />
                                </IconButton>
                                <IconButton
                                    aria-label={recording ? 'Stop recording' : 'Record audio'}
                                    onClick={recording ? stopRecording : startRecording}
                                    color={recording ? 'error' : 'default'}
                                    size="large"
                                >
                                    {recording ? <StopIcon /> : <MicIcon />}
                                </IconButton>
                                <Typography variant="body2" color="text.secondary">
                                    {recording
                                        ? 'Recording…'
                                        : files.length
                                          ? `${files.length} file${files.length === 1 ? '' : 's'}`
                                          : 'Photos or audio (optional)'}
                                </Typography>
                                {files.length ? (
                                    <Button size="small" onClick={() => setFiles([])}>
                                        Clear
                                    </Button>
                                ) : null}
                            </Stack>
                            {files.length ? (
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {files.map((item, index) => (
                                        <Chip
                                            key={`${item.name}-${item.size}-${index}`}
                                            label={item.name}
                                            onDelete={() =>
                                                setFiles(prev =>
                                                    prev.filter((_, i) => i !== index)
                                                )
                                            }
                                        />
                                    ))}
                                </Stack>
                            ) : null}
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                variant="contained"
                                onClick={handleParse}
                                disabled={!hasCapture || parsing || recording}
                                size="large"
                            >
                                {parsing ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={20} color="inherit" />
                                        <span>Reading intake…</span>
                                    </Stack>
                                ) : (
                                    'Read intake'
                                )}
                            </Button>
                            <Button
                                onClick={handleCreateFromScratch}
                                disabled={parsing || recording}
                                size="large"
                            >
                                Create from scratch
                            </Button>
                        </Stack>

                        {reviewOpen ? (
                            <>
                                <Typography variant="subtitle1" pt={1}>
                                    Review
                                </Typography>
                                <TextField
                                    label="Name"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label="Email"
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label="Phone"
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={e => setPhoneNumber(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label="Requirements"
                                    value={requirements}
                                    onChange={e => setRequirements(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                />
                                <TextField
                                    select
                                    label="Status"
                                    value={status}
                                    onChange={e => setStatus(e.target.value as LeadStatus)}
                                >
                                    {defaultLeadStatuses.map(item => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <LeadNextStepsEditor
                                    value={nextSteps}
                                    onChange={setNextSteps}
                                />
                            </>
                        ) : null}
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Button
                                onClick={() => redirect('list', 'leads')}
                                disabled={isSaving}
                                size="large"
                            >
                                Cancel
                            </Button>
                            {reviewOpen ? (
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    disabled={isSaving || parsing}
                                    size="large"
                                >
                                    {isSaving ? <CircularProgress size={20} /> : 'Save lead'}
                                </Button>
                            ) : null}
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};
